import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Terminal } from "lucide-react";
import TerminalWindow from "./TerminalWindow";

const stats = [
  { value: "1 day", label: "Websites" },
  { value: "1 week", label: "Full apps" },
  { value: "1 week", label: "AI agents" },
];

export default function Hero({ onEstimate, onAudit }) {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16">
      {/* Readability gradient over the 3D terrain */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-void/40 via-transparent to-void" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full grid lg:grid-cols-2 gap-12 lg:gap-10 items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="hero-tagline"
            className="inline-flex items-center gap-2 rounded-full border border-neon-cyan/30 bg-neon-cyan/5 backdrop-blur-md px-4 py-1.5 mb-7"
          >
            <Terminal size={14} className="text-neon-yellow" />
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-neon-cyan">
              The Infinite Code Engine
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="text-4xl sm:text-6xl lg:text-6xl font-black tracking-tighter leading-[0.95]"
          >
            We build <span className="text-neon-cyan text-glow-cyan">cutting-edge AI</span> apps, agents &amp; sites —{" "}
            <span className="text-neon-yellow">fast</span>.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 text-base sm:text-lg text-white/60 max-w-2xl leading-relaxed"
          >
            vonmal designs and ships production-grade AI products at startup speed and lean prices.
            From a <span className="text-white">jananibachpan.com</span>-style app to ACE AI agents — we deliver
            deployable software the moment it's ready.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <button
              data-testid="hero-estimate-btn"
              onClick={onEstimate}
              className="neon-btn bg-neon-cyan text-black font-semibold px-7 py-3.5 rounded-full inline-flex items-center justify-center gap-2"
            >
              Build your app <ArrowRight size={18} />
            </button>
            <button
              data-testid="hero-audit-btn"
              onClick={onAudit}
              className="border border-neon-yellow/40 hover:border-neon-yellow hover:bg-neon-yellow/5 backdrop-blur-md text-white font-semibold px-7 py-3.5 rounded-full transition-colors"
            >
              Get a Free 15-Min AI Audit
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-14 grid grid-cols-3 gap-4 max-w-xl"
          >
            {stats.map((s) => (
              <div key={s.label} className="border-l border-neon-cyan/30 pl-4">
                <div className="font-mono text-2xl sm:text-3xl font-bold text-neon-cyan">{s.value}</div>
                <div className="text-xs uppercase tracking-wider text-white/50 mt-1">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Glass terminal with auto-typing code */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <TerminalWindow />
        </motion.div>
      </div>
    </section>
  );
}
